import styled from '@emotion/styled';
import { motion } from 'framer-motion';
import { useState } from 'react';

import { Glyph } from '../../common/Glyph';
import { HomeyIcon } from '../../common/HomeyIcon';
import { variants, childVariants } from '../button/Button';
import { CapabilitySelect } from '../CapabilitySelect';
import * as Containers from '../Containers';

import { useI18n } from '../../../hooks/useI18nFormatters';
import { useCapabilityValue } from '../../../store/devices/useCapabilityValue';
import { useDevice } from '../../../store/devices/useDevices.ts';

import { scrollbar } from '../../../theme/elements/scrollbars';
import { su } from '../../../theme/functions/su';
import { glyph } from '../../../theme/glyph';
import { theme } from '../../../theme/theme';

import { getToggleImages } from './getToggleImages';

export function Toggle(props) {
  const { i18n } = useI18n();

  const { device } = useDevice({ deviceId: props.deviceId });

  const [selectedCapabilityId, setSelectedCapabilityId] = useState(() => {
    return props.component.capabilities[0];
  });

  const [value, setValue, capabilityObj] = useCapabilityValue({
    deviceId: props.deviceId,
    capabilityId: selectedCapabilityId,
  });

  if (capabilityObj == null) {
    return null;
  }

  const { urlTrue, urlFalse, useIconObj } = getToggleImages(capabilityObj.id, capabilityObj);

  function renderContent() {
    if (capabilityObj.id === 'onoff' && capabilityObj.getable === false) {
      return (
        <Toggle.GroupContainer>
          <Toggle.Wrapper>
            <Toggle.Base
              initial="initial"
              whileTap="whileTap"
              transition={{
                duration: 0.2,
              }}
              variants={variants}
              onClick={() => {
                setValue(true).catch(() => {});
              }}
            >
              <motion.span
                style={{ display: 'flex' }}
                transition={{ duration: 0.2 }}
                variants={childVariants}
              >
                <Glyph size="48px" color="currentColor" source={glyph.capability.buttonOn} />
              </motion.span>
            </Toggle.Base>

            <Toggle.Title>{i18n.messageFormatter('common.on')}</Toggle.Title>
          </Toggle.Wrapper>

          <Toggle.Wrapper>
            <Toggle.Base
              initial="initial"
              whileTap="whileTap"
              transition={{
                duration: 0.2,
              }}
              variants={variants}
              onClick={() => {
                setValue(false).catch(() => {});
              }}
            >
              <motion.span
                style={{ display: 'flex' }}
                transition={{ duration: 0.2 }}
                variants={childVariants}
              >
                <Glyph size="48px" color="currentColor" source={glyph.capability.buttonOff} />
              </motion.span>
            </Toggle.Base>

            <Toggle.Title>{i18n.messageFormatter('common.off')}</Toggle.Title>
          </Toggle.Wrapper>
        </Toggle.GroupContainer>
      );
    }

    return (
      <Toggle.Base
        initial={false}
        animate={{
          backgroundColor: value ? theme.color.white_o_100 : theme.color.black_o_50,
        }}
        onClick={() => {
          if (capabilityObj.getable === true) {
            const nextValue = !value;
            setValue(nextValue).catch(() => {});
          } else {
            setValue(true).catch(() => {});
          }
        }}
      >
        {useIconObj ? (
          <motion.span
            style={{ display: 'flex' }}
            initial={false}
            animate={{
              color: value ? theme.color.green : theme.color.white,
            }}
          >
            <HomeyIcon iconObj={capabilityObj.iconObj} size="48px" color="currentColor" />
          </motion.span>
        ) : (
          <motion.span
            style={{ display: 'flex' }}
            initial={false}
            animate={{
              color: value ? theme.color.green : theme.color.white,
            }}
          >
            <Glyph size="48px" color="currentColor" source={value ? urlTrue : urlFalse} />
          </motion.span>
        )}
      </Toggle.Base>
    );
  }

  return (
    <Containers.Control>
      <Containers.Select>
        {props.component.capabilities.length > 1 && (
          <CapabilitySelect
            selectedCapabilityId={selectedCapabilityId}
            componentCapabilities={props.component.capabilities}
            capabilitiesObj={device?.capabilitiesObj}
            onSelectionChange={setSelectedCapabilityId}
          />
        )}
      </Containers.Select>
      <Containers.Action>{renderContent()}</Containers.Action>
    </Containers.Control>
  );
}

Toggle.Base = styled(motion.button)`
  display: flex;
  position: relative;
  align-items: center;
  justify-content: center;
  width: 180px;
  height: 180px;
  border: 0;
  border-radius: 50%;
  outline: none;
  cursor: pointer;
`;

Toggle.Wrapper = styled.div`
  display: flex;
  flex: 0 0 50%;
  flex-direction: column;
  align-items: center;
  margin-bottom: ${su(2)};
`;
Toggle.Title = styled.div`
  color: ${theme.color.text_invert_o_50};
  line-height: 16px;
  text-align: center;
  padding: 0 ${su(1)};
`;

Toggle.GroupContainer = styled.div`
  ${scrollbar};
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: center;
  width: 100%;
  padding: 0 68px;
  overflow-y: auto;

  ${Toggle.Base} {
    margin: ${su(1)};
    height: 100px;
    width: 100px;
  }
`;
